import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { loginAsync, logoutAsync, clearError } from '../store/authSlice';
import { LoginRequest } from '../types/auth';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  // Session validation is handled centrally by authInterceptor.ts periodic validation
  // which properly attempts token refresh before logging out.
  // Removed redundant validateSession here that cleared auth on 401 without refresh attempt,
  // causing forced logouts during transient auth hiccups.

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const result = await dispatch(loginAsync(credentials));
      return result;
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    console.warn(
      `[LOGOUT-DEBUG] useAuth.logout | reason=user_initiated_logout | ` +
      `user=${auth.user?.username ?? 'null'} | userId=${auth.user?.id ?? 'null'} | ` +
      `wasAuthenticated=${auth.isAuthenticated} | timestamp=${new Date().toISOString()}`
    );
    await dispatch(logoutAsync());
    navigate('/onecare/login');
  }, [dispatch, navigate, auth.user, auth.isAuthenticated]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const checkTokenExpiration = useCallback(() => {
    // With cookie-based auth, expiration is handled by the server
    // Return false to indicate no client-side expiration check needed
    return false;
  }, []);

  return {
    ...auth,
    login,
    logout,
    clearError: clearAuthError,
    checkTokenExpiration,
    isAuthenticated: !!auth.token && !!auth.user,
    hasRole: (role: string) => auth.user?.role_display === role || auth.user?.role_specialty?.name === role,
    hasAnyRole: (roles: string[]) => {
      if (!auth.user) return false;
      const user = auth.user;
      return roles.some(role => 
        user.role_display === role || 
        user.role_specialty?.name === role ||
        user.role_specialty?.role_group_name === role
      );
    },
    hasHierarchyLevel: (maxLevel: number) => auth.user ? auth.user.hierarchy_level <= maxLevel : false,
    canSupervise: () => auth.user?.can_supervise || false,
    getRoleGroup: () => auth.user?.role_specialty?.role_group_name || null,
    getRoleSpecialty: () => auth.user?.role_specialty?.name || null,
  };
};
